/**
 * Carrier MCP stdio — the OCS portal password opt-in.
 *
 * WHAT THIS IS, AND WHY IT IS OPT-IN RATHER THAN AUTOMATIC
 * -------------------------------------------------------
 * Ten `ui_*` tools drive the OCS web dashboard because the OCS REST API cannot
 * do what they do. Driving it means logging into it. On the hosted Worker the
 * login lives in Clerk `privateMetadata` and is read through the Clerk Backend
 * API; the CLI has no Clerk secret and no org id, so those ten tools refused
 * unconditionally.
 *
 * A CLI operator can supply the login instead. But what they would be supplying
 * is not a scoped API token — it is the portal password, which grants the whole
 * dashboard to anyone holding it, and an environment variable is a poor place
 * for one: it lands in shell history, in `ps` output for every process on the
 * box, in CI logs and in crash dumps. `CARRIER_OCS_API_TOKEN` is a different
 * kind of secret and is not a precedent for this.
 *
 * So the capability exists and is off. Turning it on takes two independent
 * acts: setting `CARRIER_ALLOW_PORTAL_PASSWORD` to say the warning was read,
 * and supplying the credentials. Neither alone does anything. That matters
 * because the two failure modes are different — credentials pasted into a
 * shared `.env` by habit, versus a flag flipped by someone who wanted a
 * different feature — and one act cannot produce both.
 *
 * WHY THE REFUSAL DOES NOT SAY WHICH HALF IS MISSING
 * --------------------------------------------------
 * When the opt-in is incomplete the ten tools return exactly the reply they
 * returned before this change: "this needs the Worker". Identical for all three
 * incomplete states. A refusal that distinguished "flag set, credentials
 * missing" from "credentials set, flag missing" would let a caller enumerate
 * the operator's configuration one variable at a time, and the existing text is
 * shaped like a genuine structural miss on purpose.
 *
 * THE PASSWORD NEVER LEAVES THIS MODULE EXCEPT INTO THE AGENT PROMPT
 * -----------------------------------------------------------------
 * The assembled prompt carries it in plaintext because the agent types it into
 * a login form. Everything else — audit rows, tool output, dry-run previews,
 * the task record the hosted agent's own transcript lands in — goes through
 * `redactCredentials` first. This repo has already shipped a portal password
 * into KV through a hosted run's result field (#783); that is the failure mode
 * being designed against, not a hypothetical one.
 */

import type { OcsPortalCredentials } from "@carrier/ai";

/** Set to `1` or `true` to acknowledge the warning below. */
export const PORTAL_OPT_IN_ENV = "CARRIER_ALLOW_PORTAL_PASSWORD";
export const PORTAL_USERNAME_ENV = "CARRIER_OCS_PORTAL_USERNAME";
export const PORTAL_PASSWORD_ENV = "CARRIER_OCS_PORTAL_PASSWORD";

/** Overrides the dashboard the agent logs into. Rarely needed. */
export const PORTAL_DASHBOARD_ENV = "CARRIER_OCS_DASHBOARD_URL";

export type { OcsPortalCredentials };

/**
 * Why the portal path is off, when it is off.
 *
 * Callers must not surface these outward — they exist so the operator's own
 * startup warning on stderr can be specific, and so tests can assert that each
 * incomplete state still produces the one identical refusal.
 */
export type PortalOptInGap =
  | "not_requested"
  | "flag_without_credentials"
  | "credentials_without_flag";

export type PortalOptIn =
  | { enabled: false; gap: PortalOptInGap }
  | { enabled: true; credentials: OcsPortalCredentials; dashboardUrl?: string };

/**
 * The warning an operator sees on stderr when the flag is set.
 *
 * stderr, not stdout: stdout is the MCP JSON-RPC channel and a stray line there
 * corrupts the session. Printing at startup rather than only in a README is the
 * point — the ten tools are dispatched by an agent, and the human who set the
 * variable may never call one by hand.
 */
export const PORTAL_PASSWORD_WARNING = [
  `${PORTAL_OPT_IN_ENV} is set: the ten OCS-dashboard tools will use the portal login in`,
  `${PORTAL_USERNAME_ENV} / ${PORTAL_PASSWORD_ENV}.`,
  "",
  "That password is full dashboard access, not a scoped API token. Held in an environment",
  "variable it is readable from shell history, from `ps` output by any process on this",
  "machine, and from CI logs if this runs in CI. Carrier never logs it, returns it in tool",
  "output, or writes it to an audit row, but it is sent to Browser Use Cloud, which types it",
  "into the OCS login form.",
  "",
  "Each dashboard run also costs roughly $0.20-1.00 against your own BROWSER_USE_API_KEY.",
  "",
  `Unset ${PORTAL_OPT_IN_ENV} to turn this off; the tools then refuse as they did before.`,
].join("\n");

/** The one-line note appended to a gated tool's result, every time it runs. */
export const PORTAL_PASSWORD_RUN_NOTE =
  `This run used the OCS portal password from ${PORTAL_PASSWORD_ENV}, enabled by ` +
  `${PORTAL_OPT_IN_ENV}. That credential is full dashboard access and was sent to ` +
  `Browser Use Cloud to type into the OCS login form. It is not logged, audited or ` +
  `returned anywhere in this response.`;

/**
 * `1` and `true` only.
 *
 * Deliberately not "any non-empty string": `CARRIER_ALLOW_PORTAL_PASSWORD=0` and
 * `=false` read to a human as off, and a truthiness check that turned them on
 * would be the kind of quiet inversion this repo keeps finding in its own
 * gates.
 */
function flagSet(raw: string | undefined): boolean {
  if (raw === undefined) return false;
  const v = raw.trim().toLowerCase();
  return v === "1" || v === "true";
}

function nonEmpty(raw: string | undefined): string | undefined {
  if (raw === undefined) return undefined;
  return raw.length > 0 ? raw : undefined;
}

/**
 * Resolve the opt-in from the environment.
 *
 * Both halves are required and neither implies the other. A username without a
 * password (or the reverse) counts as no credentials: a half-supplied login
 * cannot log in, and treating it as present would produce a browser run that
 * burns a dollar to fail at the form.
 */
export function readPortalOptIn(
  env: NodeJS.ProcessEnv = process.env,
): PortalOptIn {
  const flag = flagSet(env[PORTAL_OPT_IN_ENV]);
  // Not trimmed: a password may legitimately begin or end with whitespace, and
  // silently altering one produces a login failure nobody can explain.
  const username = nonEmpty(env[PORTAL_USERNAME_ENV]);
  const password = nonEmpty(env[PORTAL_PASSWORD_ENV]);
  const hasCredentials = username !== undefined && password !== undefined;

  if (flag && hasCredentials) {
    return {
      enabled: true,
      credentials: { username, password },
      ...(nonEmpty(env[PORTAL_DASHBOARD_ENV])
        ? { dashboardUrl: env[PORTAL_DASHBOARD_ENV] }
        : {}),
    };
  }
  if (flag) return { enabled: false, gap: "flag_without_credentials" };
  if (hasCredentials)
    return { enabled: false, gap: "credentials_without_flag" };
  return { enabled: false, gap: "not_requested" };
}

/**
 * Print the warning at startup, where the operator will see it.
 *
 * It fires whenever the flag is set — including when the credentials are
 * missing, because someone who set the flag intends to supply them and should
 * read the warning before they do, not after.
 *
 * `credentials_without_flag` gets a different, shorter line: credentials are
 * sitting in this process's environment doing nothing, which is worth saying
 * out loud, but it is not the warning about a capability that is now live.
 */
export function warnPortalOptIn(
  optIn: PortalOptIn,
  write: (line: string) => void = (line) => console.error(line),
): void {
  if (optIn.enabled) {
    write(PORTAL_PASSWORD_WARNING);
    return;
  }
  if (optIn.gap === "flag_without_credentials") {
    write(PORTAL_PASSWORD_WARNING);
    write(
      `${PORTAL_USERNAME_ENV} and ${PORTAL_PASSWORD_ENV} are not both set, so the ` +
        `OCS-dashboard tools stay disabled.`,
    );
    return;
  }
  if (optIn.gap === "credentials_without_flag") {
    write(
      `${PORTAL_PASSWORD_ENV} is set but ${PORTAL_OPT_IN_ENV} is not, so the ` +
        `OCS-dashboard tools stay disabled and this portal password is unused. ` +
        `Unset it unless you mean to opt in.`,
    );
  }
}
